// Phase 4 — Documents page (central repository for attachments)
import { useEffect, useState, useCallback, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Upload, FileText, Image as ImageIcon, Download, Filter, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import { downloadAttachment } from "@/lib/downloadAttachment";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/permissions";
import RecordTrail from "@/components/RecordTrail";

const CATEGORIES = ["invoice", "receipt", "payment_voucher", "bank_statement", "payroll_schedule",
                    "tax_document", "contract", "government_letter", "financial_report",
                    "reconciliation", "approval_note", "other"];

function fileIcon(mime) {
  if (!mime) return <FileText className="h-10 w-10 text-slate-400" />;
  if (mime.startsWith("image/")) return <ImageIcon className="h-10 w-10 text-[#4FA3D1]" />;
  if (mime.includes("pdf")) return <FileText className="h-10 w-10 text-red-500" />;
  return <FileText className="h-10 w-10 text-slate-400" />;
}

export default function DocumentsPage() {
  const { user } = useAuth();
  const role = user?.role;
  const canUpload = [ROLES.SYSTEM_ADMIN, ROLES.SALIHI_ACCOUNTANT, ROLES.SALIHI_SENIOR_ACCOUNTANT,
                     ROLES.SALIHI_FINANCE_MANAGER, ROLES.SALIHI_COMPLIANCE_OFFICER].includes(role)
                    || user?.full_access === true;
  const isAdmin = role === ROLES.SYSTEM_ADMIN || user?.full_access === true;

  const [items, setItems] = useState([]);
  const [filterCat, setFilterCat] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [drawer, setDrawer] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const [form, setForm] = useState({ category: "invoice", tags: "", notes: "" });
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const adminDeleteDoc = async () => {
    if (!confirmDel) return;
    setDelBusy(true);
    try {
      await api.delete(`/documents/${confirmDel.id}`);
      toast.success("Document deleted.");
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDelBusy(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCat) params.category = filterCat;
      if (filterTag) params.tag = filterTag;
      const { data } = await api.get("/documents", { params });
      setItems(data.items);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filterCat, filterTag]);

  useEffect(() => { load(); }, [load]);

  async function doUpload() {
    if (!fileRef.current?.files?.[0]) { toast.error("Pick a file"); return; }
    const file = fileRef.current.files[0];
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", form.category);
      fd.append("tags", form.tags);
      fd.append("notes", form.notes);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setOpen(false); setForm({ category: "invoice", tags: "", notes: "" });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  }

  function downloadDoc(d) {
    // P0 (2026-07-14) — route via helper so 401/410 messages are consistent
    // and future callers pick up the same fetch-blob pattern.
    downloadAttachment(d.id, d.original_name);
  }

  return (
    <div className="space-y-5" data-testid="documents-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold">Documents</h2>
          <p className="text-sm text-slate-500 mt-1">Central document repository — invoices, receipts, statements, contracts and tax filings.</p>
        </div>
        {canUpload && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" data-testid="upload-doc-btn">
                <Upload className="h-4 w-4 mr-1.5" /> Upload document
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Upload document</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>File</Label>
                  <Input type="file" ref={fileRef} accept="application/pdf,image/png,image/jpeg,image/webp" data-testid="doc-file" />
                </div>
                <div><Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="doc-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Tags (comma-separated)</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="e.g. May-2026, supplier" data-testid="doc-tags" /></div>
                <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button disabled={uploading} onClick={doUpload} className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" data-testid="save-doc-btn">
                  {uploading && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Upload
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <Filter className="h-4 w-4 text-slate-400 mb-2.5" />
        <div>
          <Label className="text-xs">Category</Label>
          <Select value={filterCat || "all"} onValueChange={(v) => setFilterCat(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48" data-testid="doc-filter-cat"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Tag</Label><Input className="w-44" value={filterTag} onChange={(e) => setFilterTag(e.target.value)} placeholder="search by tag" data-testid="doc-filter-tag" /></div>
      </div>

      {loading
        ? <div className="flex justify-center py-10"><Loader2 className="animate-spin h-6 w-6 text-[#4FA3D1]" /></div>
        : items.length === 0
          ? <Card><CardContent className="py-12 text-center text-slate-400">No documents.</CardContent></Card>
          : <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" data-testid="documents-grid">
              {items.map((d) => (
                <Card key={d.id} className="hover:shadow-md transition-shadow cursor-pointer relative" onClick={() => setDrawer(d)} data-testid={`doc-card-${d.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-center h-20">{fileIcon(d.mime_type)}</div>
                    <p className="mt-3 text-sm font-medium truncate" title={d.original_name}>{d.original_name}</p>
                    <p className="text-xs text-slate-500 mt-1">{d.uploaded_at?.slice(0, 10)}</p>
                    {d.category && <Badge className="mt-2 bg-[#4FA3D1]/10 text-[#0B1929] border-0">{d.category}</Badge>}
                  </CardContent>
                  {isAdmin && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setConfirmDel(d); }}
                      className="absolute top-2 right-2 p-1.5 rounded bg-white/90 hover:bg-rose-50 text-rose-600 shadow-sm border border-rose-100"
                      title="Delete document (admin override)"
                      data-testid={`admin-delete-doc-${d.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </Card>
              ))}
            </div>
      }

      <Sheet open={!!drawer} onOpenChange={(o) => !o && setDrawer(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          {drawer && <>
            <SheetHeader><SheetTitle>{drawer.original_name}</SheetTitle></SheetHeader>
            <div className="space-y-3 mt-4 text-sm">
              <div><span className="text-slate-500">Category</span><div>{drawer.category ? <Badge>{drawer.category}</Badge> : "—"}</div></div>
              <div><span className="text-slate-500">Tags</span><div className="flex flex-wrap gap-1 mt-1">{(drawer.tags || []).map((t, i) => <Badge key={i} variant="secondary">{t}</Badge>) || "—"}</div></div>
              <div><span className="text-slate-500">Uploaded</span><div>{drawer.uploaded_at?.slice(0, 19).replace("T", " ")} by {drawer.uploaded_by_email}</div></div>
              <div><span className="text-slate-500">Size</span><div>{Math.round((drawer.size_bytes || 0) / 1024)} KB · {drawer.mime_type}</div></div>
              {drawer.notes && <div><span className="text-slate-500">Notes</span><p className="mt-1">{drawer.notes}</p></div>}
              <Button onClick={() => downloadDoc(drawer)} className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white w-full" data-testid="download-doc-btn">
                <Download className="h-4 w-4 mr-1.5" /> Download
              </Button>
              <RecordTrail entity_type="document" entity_id={drawer.id} record={drawer} />
            </div>
          </>}
        </SheetContent>
      </Sheet>
    </div>
  );
}
